import { ThemeComponent } from '@/theme/ThemeComponent';
import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { cart } from '@/theme/Cart';
import { QuantitySelector } from '../QuantitySelector/QuantitySelector';

@Component
export class CartItem extends ThemeComponent {
    @Prop item!: Shopify.Liquid.LineItem;

    @Ref({ required: false }) quantitySelector!: QuantitySelector[];
    @Ref({ required: false }) removeItemButton!: HTMLButtonElement[];
    @Ref({ required: false }) errorMessage!: HTMLElement;

    private errorMsg: string = '';
    private domRoot: HTMLElement | null = null;
    private quantitySelectorsAttached: boolean = false;
    private quantityInputTimeout: ReturnType<typeof setTimeout> | null = null;
    private isHandlingQuantity: boolean = false;

    init() {
        // Check if cart contains a gift card
        const hasGiftCard = cart.items.some((item) => item.handle === 'gift-card');
        const checkoutButton = document.querySelector('.cart__checkout-button') as HTMLElement;

        if (checkoutButton && hasGiftCard) {
            checkoutButton.classList.remove('button--disabled');
        }

        this.domRoot = this.getDomRoot();
        this.attachEventListeners();

        // Global delegated fallback for remove buttons (handles hoisted DOM)
        this.$listen(window, 'click', (event) => {
            const target = event.target as HTMLElement | null;
            if (!target) {
                return;
            }
            const button = target.closest('[data-ref="removeItemButton"]') as HTMLButtonElement | null;
            if (!button) {
                return;
            }
            const lineItemId = Number(button.dataset.lineItemId || '');
            const variantId = Number(button.dataset.variantId || '');
            const matchesLine = lineItemId === this.item.id;
            const matchesVariant = variantId === this.item.variant_id;
            if (!matchesLine && !matchesVariant) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            this.handleQuantity(0).catch(() => {
                // Silently handle errors
            });
        });

        // Global delegated handler for quantity selector buttons as a fallback
        // This ensures updates work even if subscriptions aren't set up yet
        // Use capture phase to handle before QuantitySelector's handlers
        this.$listen(window, 'click', (event) => {
            const target = event.target as HTMLElement | null;
            if (!target) {
                return;
            }

            // Check if click is on a quantity selector button (increase/decrease)
            const quantityButton = target.closest(
                '[data-ref="increaseButton"], [data-ref="decreaseButton"]'
            ) as HTMLButtonElement | null;
            if (!quantityButton) {
                return;
            }

            // Find the quantity-selector component that contains this button
            const quantitySelector = quantityButton.closest('quantity-selector') as QuantitySelector | null;
            if (!quantitySelector) {
                return;
            }

            // Find the cart-item that contains this quantity selector (handles hoisted DOM)
            const cartItemElement = quantitySelector.closest('cart-item, tr[item]') as HTMLElement | null;
            if (!cartItemElement) {
                return;
            }

            // Check if this is our cart item by comparing item IDs
            let cartItemMatches = false;
            if (cartItemElement === this || this.contains(cartItemElement) || cartItemElement.contains(this)) {
                cartItemMatches = true;
            } else {
                // Check by item attribute for hoisted DOM
                const itemAttr = cartItemElement.getAttribute('item');
                if (itemAttr) {
                    try {
                        const parsedItem = JSON.parse(itemAttr) as Shopify.Liquid.LineItem;
                        cartItemMatches = parsedItem.id === this.item.id ||
                            parsedItem.variant_id === this.item.variant_id;
                    } catch {
                        // Ignore parse errors
                    }
                }
            }

            if (!cartItemMatches) {
                return;
            }

            // Stop the event from reaching QuantitySelector's handlers FIRST to prevent double updates
            event.stopPropagation();
            event.preventDefault();

            const input = quantitySelector.querySelector('[data-ref="input"]') as HTMLInputElement | null;
            if (!input) {
                return;
            }

            // Determine if increase or decrease based on which button was clicked
            const isIncrease = quantityButton.hasAttribute('data-ref') &&
                               quantityButton.getAttribute('data-ref') === 'increaseButton';

            // Use this.item.quantity (from Liquid prop) as the source of truth
            // This is always current because it comes from server-side rendering
            // cart.items may be stale when returning from checkout
            // Type assertion needed because Shopify.Liquid.LineItem.quantity is typed as unknown
            const currentValue = Number(this.item.quantity) || 0;
            const newValue = isIncrease ? currentValue + 1 : Math.max(0, currentValue - 1);

            // Update the input value directly (mimicking what QuantitySelector would do)
            input.value = newValue.toString();

            // Also update QuantitySelector's internal value if it has a value setter
            // This keeps the component's internal state in sync
            if (quantitySelector && 'value' in quantitySelector) {
                try {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (quantitySelector as any).value = newValue;
                } catch {
                    // If value is read-only, that's okay
                }
            }

            // Update the cart
            this.handleQuantity(newValue).catch(() => {
                // Silently handle errors
            });
        });

        // Also handle direct input changes on quantity selector inputs
        this.$listen(window, 'input', (event) => {
            const target = event.target as HTMLElement | null;
            if (!target || target.tagName !== 'INPUT') {
                return;
            }

            // Check if it's a quantity selector input
            const quantitySelector = target.closest('quantity-selector') as QuantitySelector | null;
            if (!quantitySelector) {
                return;
            }

            // Find the cart-item that contains this quantity selector (handles hoisted DOM)
            const cartItemElement = quantitySelector.closest('cart-item, tr[item]') as HTMLElement | null;
            if (!cartItemElement) {
                return;
            }

            // Check if this is our cart item by comparing item IDs
            let cartItemMatches = false;
            if (cartItemElement === this || this.contains(cartItemElement) || cartItemElement.contains(this)) {
                cartItemMatches = true;
            } else {
                // Check by item attribute for hoisted DOM
                const itemAttr = cartItemElement.getAttribute('item');
                if (itemAttr) {
                    try {
                        const parsedItem = JSON.parse(itemAttr) as Shopify.Liquid.LineItem;
                        cartItemMatches = parsedItem.id === this.item.id ||
                            parsedItem.variant_id === this.item.variant_id;
                    } catch {
                        // Ignore parse errors
                    }
                }
            }

            if (!cartItemMatches) {
                return;
            }

            const input = target as HTMLInputElement;
            const newValue = Number(input.value) || 0;

            // Debounce the input to avoid too many updates
            if (this.quantityInputTimeout) {
                clearTimeout(this.quantityInputTimeout);
            }
            this.quantityInputTimeout = setTimeout(() => {
                this.handleQuantity(newValue).catch(() => {
                    // Silently handle errors
                });
            }, 500);
        });

        // Re-attach listeners after cart updates (when DOM is replaced)
        document.addEventListener('cartChanged', () => {
            // Reset the flag
            this.quantitySelectorsAttached = false;
            // Also reset the handling flag in case it got stuck
            this.isHandlingQuantity = false;
            // Note: We don't need to reattach listeners since we use delegated handlers on window
            // But we do need to update domRoot for other operations
            setTimeout(() => {
                this.domRoot = this.getDomRoot();
            }, 100);
        });
    }

    private getDomRoot(): HTMLElement | null {
        // Try to find the inner div first
        const innerRoot = this.querySelector('.cart-item__item-inner') as HTMLElement | null;
        if (innerRoot) {
            return innerRoot;
        }

        // Try to find the <tr> element (may be hoisted out of custom element)
        const variantId = this.item.id;
        const table = this.closest('table');
        if (table) {
            const rows = table.querySelectorAll('tr[item]');
            for (const row of rows) {
                try {
                    const itemAttr = row.getAttribute('item');
                    if (itemAttr) {
                        const parsedItem = JSON.parse(itemAttr) as Shopify.Liquid.LineItem;
                        if (parsedItem.id === variantId) {
                            return row as HTMLElement;
                        }
                    }
                } catch {
                    // Ignore parse errors
                }
            }
        }

        // Fallback: check siblings
        const neighbours: (Element | null)[] = [this.nextElementSibling, this.previousElementSibling];
        for (const neighbour of neighbours) {
            if (neighbour instanceof HTMLElement && neighbour.classList.contains('cart-item')) {
                try {
                    const itemAttr = neighbour.getAttribute('item');
                    if (itemAttr) {
                        const parsedItem = JSON.parse(itemAttr) as Shopify.Liquid.LineItem;
                        if (parsedItem.id === variantId) {
                            return neighbour;
                        }
                    }
                } catch {
                    // Ignore parse errors
                }
            }
        }

        return null;
    }

    private attachEventListeners(attempt: number = 0) {
        const root = this.getDomRoot();
        if (!root) {
            if (attempt < 5) {
                setTimeout(() => this.attachEventListeners(attempt + 1), 100);
            }
            return;
        }

        // Find remove button relative to the actual DOM root
        let removeButtons = Array.from(
            root.querySelectorAll('[data-ref="removeItemButton"]') as NodeListOf<HTMLButtonElement>
        );

        if (removeButtons.length === 0) {
            const lineSelector = `[data-line-item-id="${this.item.id}"]`;
            const variantSelector = `[data-variant-id="${this.item.variant_id}"]`;
            removeButtons = Array.from(document.querySelectorAll(
                `${lineSelector}, ${variantSelector}`
            )) as HTMLButtonElement[];
        }

        // Update refs
        this.removeItemButton = removeButtons as unknown as HTMLButtonElement[];

        // Attach click listeners
        for (const button of removeButtons) {
            this.$listen(button, 'click', () => this.handleQuantity(0));
        }

        // Quantity selectors should still work via @Ref since they're components
        // But we also need to manually find them in case @Ref hasn't populated yet after DOM replacement
        let quantitySelectors: QuantitySelector[] = [];

        // First try @Ref (should work if components are initialized)
        if (this.quantitySelector && this.quantitySelector.length > 0) {
            quantitySelectors = [...this.quantitySelector];
        }

        // Also manually query for quantity-selector components as a fallback
        // This ensures we find them even if @Ref hasn't populated yet
        // Try both from root and from this element (in case DOM is hoisted)
        let manualSelectors = Array.from(
            root.querySelectorAll('quantity-selector')
        ) as QuantitySelector[];

        // Also try querying from this element directly
        const thisSelectors = Array.from(
            this.querySelectorAll('quantity-selector')
        ) as QuantitySelector[];
        manualSelectors = [...manualSelectors, ...thisSelectors];

        // Combine both sources, removing duplicates
        const allSelectors = new Set<QuantitySelector>();
        for (const selector of quantitySelectors) {
            if (selector) {
                allSelectors.add(selector);
            }
        }
        for (const selector of manualSelectors) {
            if (selector) {
                allSelectors.add(selector);
            }
        }

        // Note: We're not subscribing to QuantitySelector onChange events anymore
        // Instead, we use delegated click handlers on the buttons (set up in init())
        // This avoids issues with subscriptions not working after DOM replacement
        // and prevents double updates
        this.quantitySelectorsAttached = true;
    }

    async handleQuantity(quantity: number) {
        // Prevent duplicate calls
        if (this.isHandlingQuantity) {
            return;
        }

        // Find matching cart item first to verify current quantity
        let matchingItem: (typeof cart.items)[number] | null = null;
        const targetLineItemId = this.item.id;
        const targetVariantId = this.item.variant_id;

        for (const item of cart.items) {
            if (item.id === targetLineItemId || item.variantId === targetVariantId) {
                matchingItem = item;
                break;
            }
        }

        // If no matching item found, cart state might be stale - reload and try again
        if (matchingItem === null) {
            await cart.reload();
            // Try to find again after reload
            for (const item of cart.items) {
                if (item.id === targetLineItemId || item.variantId === targetVariantId) {
                    matchingItem = item;
                    break;
                }
            }
            // If still not found, can't proceed
            if (matchingItem === null) {
                return;
            }
        }

        // If quantity matches current cart quantity, no update needed (prevents unnecessary updates)
        if (matchingItem.quantity === quantity) {
            return;
        }


        this.isHandlingQuantity = true;

        // Auto-reset the flag after a timeout as a safety measure
        setTimeout(() => {
            this.isHandlingQuantity = false;
        }, 2000);

        this.toggleGlobalSpinner(true);
        try {
            if (quantity === 0) {
                // Remove item
                await cart.removeItem(matchingItem);
            } else {
                // Change item
                await cart.changeItem(matchingItem, quantity, null);
            }

            // Clear error if update was successful
            this.errorMsg = '';
            this.hideErrorMessage();

        } catch (event: unknown) {
            const { message } = event as Error;
            this.errorMsg = `${message ? message.split('.')[0] : ''}`;
            this.showErrorMessage();
        } finally {
            this.toggleGlobalSpinner(false);
            this.isHandlingQuantity = false;
        }

        // Dispatch event for other components to listen to
        document.dispatchEvent(new Event('cartChanged'));
    }

    private toggleGlobalSpinner(isActive: boolean) {
        const spinner = document.querySelector('[data-ref="updatingCart"]') as HTMLElement | null;
        if (!spinner) {
            return;
        }
        spinner.classList.toggle('loading-spinner--active', isActive);
    }

    showErrorMessage() {
        const cartWrapper = document.querySelector('.cart-error');
        if (cartWrapper && this.errorMsg) {
            cartWrapper.textContent = this.errorMsg;
            cartWrapper.classList.add('show');
        }
    }

    hideErrorMessage() {
        const cartWrapper = document.querySelector('.cart-error');
        if (cartWrapper) {
            cartWrapper.textContent = '';
            cartWrapper.classList.remove('show');
        }
    }


}
